import { createAsyncReducers } from '../reducers/lib';
import { createAsyncState } from '../state/lib';
import { HttpVerb } from '@sonar-software/sonar-http';
import { AsyncDeleteRedux, AsyncGetRedux, AsyncPatchRedux, AsyncPostRedux, AsyncPutRedux, AsyncRedux } from './types';
import { AsyncReduxOptions, AsyncReduxVerbOptions } from '../types/Options';
import { createAsyncActionCreators, createAsyncActionCreatorsMap } from '../actionCreators/lib';
import { createAsyncActionTypes, createAsyncActionTypesMap } from '../actionTypes/lib';

export function createAsyncRedux<HV extends HttpVerb, N extends string, RQ = void, RC = void>(
	options: AsyncReduxOptions<HV, N>,
	defaultRequest?: RQ
): AsyncRedux<HV, N, RQ, RC> {
	const asyncActionCreatorsMap = createAsyncActionCreatorsMap<HV, N, RQ, RC>(options, defaultRequest);

	return {
		asyncActionCreators: createAsyncActionCreators<HV, N, RQ, RC>(options, asyncActionCreatorsMap),
		asyncActionCreatorsMap,
		asyncActionTypes: createAsyncActionTypes<HV, N>(options),
		asyncActionTypesMap: createAsyncActionTypesMap<HV, N>(options),
		asyncReducers: createAsyncReducers<HV, N, RQ, RC>(options),
		asyncState: createAsyncState<RC>()
	};
}

export function createDeleteAsyncRedux<N extends string, RQ = void, RC = void>(
	options: AsyncReduxVerbOptions<N>,
	defaultRequest?: RQ
): AsyncDeleteRedux<N, RQ, RC> {
	return createAsyncRedux<HttpVerb.Delete, N, RQ, RC>(
		{
			...options,
			httpVerb: HttpVerb.Delete
		},
		defaultRequest
	);
}

export function createGetAsyncRedux<N extends string, RQ = void, RC = void>(
	options: AsyncReduxVerbOptions<N>,
	defaultRequest?: RQ
): AsyncGetRedux<N, RQ, RC> {
	return createAsyncRedux<HttpVerb.Get, N, RQ, RC>(
		{
			...options,
			httpVerb: HttpVerb.Get
		},
		defaultRequest
	);
}

export function createPatchAsyncRedux<N extends string, RQ = void, RC = void>(
	options: AsyncReduxVerbOptions<N>,
	defaultRequest?: RQ
): AsyncPatchRedux<N, RQ, RC> {
	return createAsyncRedux<HttpVerb.Patch, N, RQ, RC>(
		{
			...options,
			httpVerb: HttpVerb.Patch
		},
		defaultRequest
	);
}

export function createPostAsyncRedux<N extends string, RQ = void, RC = void>(
	options: AsyncReduxVerbOptions<N>,
	defaultRequest?: RQ
): AsyncPostRedux<N, RQ, RC> {
	return createAsyncRedux<HttpVerb.Post, N, RQ, RC>(
		{
			...options,
			httpVerb: HttpVerb.Post
		},
		defaultRequest
	);
}

export function createPutAsyncRedux<N extends string, RQ = void, RC = void>(
	options: AsyncReduxVerbOptions<N>,
	defaultRequest?: RQ
): AsyncPutRedux<N, RQ, RC> {
	return createAsyncRedux<HttpVerb.Put, N, RQ, RC>(
		{
			...options,
			httpVerb: HttpVerb.Put
		},
		defaultRequest
	);
}
